/**
 * The feedback channel of a homework upload (#469, homework-feedback@1), drawn apart from the verdicts.
 *
 * - **It is not a check.** A flag is "look at this line again": no ✓, no ✗, no verdict colour, no word like "correct" or
 *   "looks fine". A line the reviewer did not point at shows nothing at all (`not_flagged` is not a statement that the line
 *   is right). Frames are dashed in the plum family, the verdict frames' own colours are never used.
 * - **Showing the lines is recorded.** "Check this line" is itself help (the `error_location` rung), so the lines are not drawn from a
 *   read: once the review is `produced` the section asks the server to open them (`POST .../feedback-channel/open`, which records the
 *   showing first) and draws only what that response returns. A plain read of the channel carries no flag until they were shown, and
 *   whatever flags it does carry are ignored here, so every time the section is displayed it is a showing the server recorded.
 * - **The reason is pulled, never pushed.** The default is "Check this line". The reason is fetched only by pressing "Why?",
 *   which the server records as help already given (so the button states the cost before it is pressed); no prefetch, no
 *   hover. The reason is the model's text: untrusted, rendered as plain text, kept in memory only.
 * - No count, score or percentage, and nothing about what the learner knows.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HomeworkFeedbackChannelResponse } from '@davinci/contracts';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { CHANNEL_NO_RETRY, useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { UntrustedText } from './untrusted-text.js';
import { feedbackChrome } from './feedback-chrome.js';
import { pageImageUrl } from './feedback-page-image.js';
import { POLL_INTERVAL_MS } from './polling.js';

type Flag = HomeworkFeedbackChannelResponse['flags'][number];

/** The failure that no retry fixes until the learner does something: the exercise's question sheet is not available (#660). */
const NO_SHEET = 'no_question_sheet';

/**
 * The second reader (the reviewer) could not run because the exercise's question sheet is unavailable (#661). The failure is the reviewer's,
 * not the kernel's: ✓ and ✗ can sit beside this card, so with any decided note on the sheet the heading says the second reader could not
 * run, and "Not checked yet" is only for a sheet where nothing was decided. It promises no check: the sheet can stay unavailable for reasons
 * Moodle cannot fix. It names Moodle and offers the way to connect it only when the Moodle connection is read and known not to be connected.
 * Connected, or not yet known, it says only the plain fact. The retry the channel always offered stays.
 */
export function NoSheetCard({ onRetry, retrying = false, anyDecided = false }: { readonly onRetry: () => void; readonly retrying?: boolean; readonly anyDecided?: boolean }): React.JSX.Element {
  const t = useFeedbackLabels();
  const m = useMessages(feedbackChrome);
  const api = useApi();
  const connection = useQuery({ queryKey: queryKeys.moodleConnection, queryFn: () => api.getMoodleConnection(), retry: false });
  const notConnected = connection.data !== undefined && !connection.data.connected;
  return (
    <section className="fb-no-moodle" aria-labelledby="fb-no-moodle-heading" role="alert">
      <h2 id="fb-no-moodle-heading">{anyDecided ? m.secondReaderCouldNotRun : m.notCheckedYetHeading}</h2>
      {notConnected ? (
        <>
          <p>{m.needsMoodle}</p>
          <Link className="fb-connect-moodle" to="/settings/account#moodle-connection">
            {m.connectMoodle}
          </Link>
        </>
      ) : (
        <p>{t.channelFailed(NO_SHEET)}</p>
      )}
      <button type="button" className="fb-no-moodle-retry" disabled={retrying} onClick={onRetry}>
        {t.channelRetryAction}
      </button>
    </section>
  );
}

/**
 * The latest review ran on the learner's pages alone, because the learner has no question sheet. It promises nothing about what connecting
 * would do, and it is about the reviewer's input, not a verdict. The link shows only when Moodle is known not to be connected.
 */
export function WithoutSheetNote(): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const api = useApi();
  const connection = useQuery({ queryKey: queryKeys.moodleConnection, queryFn: () => api.getMoodleConnection(), retry: false });
  const notConnected = connection.data !== undefined && !connection.data.connected;
  return (
    <section className="fb-without-sheet" role="status" aria-labelledby="fb-without-sheet-heading">
      <div>
        <h2 id="fb-without-sheet-heading">{m.withoutSheetHead}</h2>
        <p>{m.withoutSheetLine}</p>
      </div>
      {notConnected ? (
        <Link className="fb-without-sheet-connect" to="/settings/account#moodle-connection">
          {m.connectMoodle}
        </Link>
      ) : null}
    </section>
  );
}

/** What the page reads from the channel: which lines the reviewer pointed at (shown, so recorded), and whether the sheet is missing. */
export interface ChannelSummary {
  readonly flaggedRegionIds: readonly string[];
  readonly noSheet: boolean;
  /** The latest review ran on the learner's pages alone, because the learner has no question sheet. */
  readonly withoutSheet: boolean;
  /** The `check_this` lines the open call showed (so recorded): the page draws each as a look-here block of its own (#661). */
  readonly flags: readonly Flag[];
  /** What the learner has done with each flag's "Why?", and the press: the page's note holds the rung, this section holds the state. */
  readonly reasons: Readonly<Record<string, ReasonState>>;
  readonly why: (regionId: string) => void;
}

export interface ChannelPage {
  readonly pageId: string;
  readonly pageIndex: number;
  readonly widthPx: number;
  readonly heightPx: number;
}

/** What the learner has done with one flag's reason, in memory only. */
export type ReasonState = { readonly status: 'loading' } | { readonly status: 'failed' } | { readonly status: 'shown'; readonly reason: string };

export interface FeedbackChannelViewProps {
  readonly channel: HomeworkFeedbackChannelResponse;
  readonly pages: readonly ChannelPage[];
  readonly reasons: Readonly<Record<string, ReasonState>>;
  readonly retrying?: boolean;
  /** A check run exists for the upload: only then can feedback be asked for. */
  readonly checked?: boolean;
  readonly onWhy: (regionId: string) => void;
  readonly onRetry: () => void;
  /** The lines to check could not be shown (the open call failed); a button asks again. */
  readonly openFailed?: boolean;
  readonly onOpen?: () => void;
  /** The "question sheet unavailable" card is drawn elsewhere by the caller, so this section draws nothing for that failure. */
  readonly hoistNoSheet?: boolean;
  /** The flags that have a page are drawn on it by the caller (#661), so this section keeps only its states and the flags that have no page. */
  readonly flagsOnPage?: boolean;
}

export function FeedbackChannelView({ channel, pages, reasons, retrying = false, checked = false, onWhy, onRetry, openFailed = false, onOpen, hoistNoSheet = false, flagsOnPage = false }: FeedbackChannelViewProps): React.JSX.Element | null {
  const t = useFeedbackLabels();
  if (channel.feedbackState === 'none' && !checked) return null;
  const noSheet = channel.feedbackState === 'failed' && channel.failureKind === NO_SHEET;
  if (noSheet && hoistNoSheet) return null;

  const shown = channel.feedbackState === 'produced' ? channel.flags.filter((flag) => flag.flag === 'check_this') : [];
  const numbered = shown.map((flag, index) => ({ flag, number: index + 1 }));
  const pageOf = (pageId: string | null) => pages.find((page) => page.pageId === pageId);
  const placed = flagsOnPage ? [] : numbered.filter((entry) => pageOf(entry.flag.pageId) !== undefined);
  const unplaced = numbered.filter((entry) => pageOf(entry.flag.pageId) === undefined);
  // Everything this section would say is on the page: draw nothing, not a heading above nothing.
  if (flagsOnPage && channel.feedbackState === 'produced' && unplaced.length === 0 && !openFailed) return null;
  const pageIds = [...new Set(placed.map((entry) => entry.flag.pageId as string))].sort(
    (a, b) => (pageOf(a)?.pageIndex ?? 0) - (pageOf(b)?.pageIndex ?? 0),
  );

  // Nothing requested and nothing offered: one quiet line, the heading kept for assistive technology.
  const quiet = channel.feedbackState === 'not_requested' && !openFailed;

  const card = ({ flag, number }: { flag: Flag; number: number }) => {
    const reason = reasons[flag.regionId];
    return (
      <div key={flag.regionId} className="channel-flag">
        <h4>
          <span className="channel-flag-number">{t.channelFlagName(number)}</span>
        </h4>
        <p>{t.channelFlagDefault}</p>
        {flag.location?.kind === 'quote' && flag.location.reason !== undefined ? <p className="field-hint">{t.quoteReason(flag.location.reason)}</p> : null}
        {flag.reasonAvailable && reason?.status !== 'shown' ? (
          <>
            <button type="button" disabled={reason?.status === 'loading'} onClick={() => onWhy(flag.regionId)}>
              {t.channelWhyAction}
            </button>
            <p className="field-hint">{t.channelWhyCost}</p>
          </>
        ) : null}
        {reason?.status === 'loading' ? <p role="status">{t.channelReasonLoading}</p> : null}
        {reason?.status === 'failed' ? (
          <p role="alert" className="field-hint">
            {t.channelReasonFailed}
          </p>
        ) : null}
        {reason?.status === 'shown' ? (
          <>
            <p className="channel-reason" dir="auto">
              <UntrustedText text={reason.reason} />
            </p>
            <p className="field-hint">{t.channelWhyCost}</p>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <section className="feedback-channel" data-quiet={quiet} aria-labelledby="feedback-channel-heading">
      <h2 id="feedback-channel-heading" className={quiet ? 'visually-hidden' : undefined}>
        {t.channelHeading}
      </h2>
      {channel.feedbackState === 'not_requested' ? (
        <p className="field-hint">{t.channelNothing}</p>
      ) : (
        <p>{t.channelIntro}</p>
      )}
      {channel.feedbackState === 'pending' ? <p role="status">{t.channelPending}</p> : null}
      {openFailed ? (
        <div className="notice notice-error" role="alert">
          <p>{t.channelOpenFailed}</p>
          {onOpen === undefined ? null : (
            <button type="button" onClick={onOpen}>
              {t.channelRetryAction}
            </button>
          )}
        </div>
      ) : null}
      {channel.feedbackState === 'none' ? (
        <button type="button" disabled={retrying} onClick={onRetry}>
          {t.channelGetAction}
        </button>
      ) : null}
      {channel.feedbackState === 'withheld_model_drift' ? (
        <div className="notice" role="status">
          <p>{t.channelDrift}</p>
        </div>
      ) : null}
      {noSheet ? <NoSheetCard onRetry={onRetry} retrying={retrying} /> : null}
      {channel.feedbackState === 'failed' && !noSheet ? (
        <div className="notice notice-error" role="alert">
          <p>{t.channelFailed(channel.failureKind)}</p>
          {channel.failureKind !== null && CHANNEL_NO_RETRY.has(channel.failureKind) ? null : (
            <button type="button" disabled={retrying} onClick={onRetry}>
              {t.channelRetryAction}
            </button>
          )}
        </div>
      ) : null}
      {pageIds.map((pageId) => {
        const page = pageOf(pageId) as ChannelPage;
        const entries = placed.filter((entry) => entry.flag.pageId === pageId);
        const marker = Math.max(24, Math.round(page.widthPx / 40));
        return (
          <section key={pageId} aria-labelledby={`channel-page-${pageId}`}>
            <h3 id={`channel-page-${pageId}`}>{t.channelPageHeading(page.pageIndex + 1)}</h3>
            <svg
              className="feedback-page-image"
              viewBox={`0 0 ${String(page.widthPx)} ${String(page.heightPx)}`}
              role="img"
              aria-label={t.pageImageLabel(page.pageIndex + 1)}
            >
              <image href={pageImageUrl(pageId)} x={0} y={0} width={page.widthPx} height={page.heightPx} />
              {entries.map(({ flag, number }) => {
                if (flag.location?.kind !== 'frame' || flag.location.bbox === undefined) return null;
                const { x0, y0, x1, y1 } = flag.location.bbox;
                return (
                  <g key={flag.regionId} className="channel-frame">
                    <rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} strokeDasharray={`${String(marker / 2)} ${String(marker / 3)}`} />
                    <circle cx={x0} cy={y0} r={marker / 2} />
                    <text x={x0} y={y0} dy="0.35em" textAnchor="middle" fontSize={marker * 0.6}>
                      {number}
                    </text>
                  </g>
                );
              })}
            </svg>
            {entries.map(card)}
          </section>
        );
      })}
      {unplaced.map(card)}
    </section>
  );
}

/**
 * The channel of one upload: polls (a read) while the review is pending; once it is produced, opens its flags (a recorded showing) and
 * draws those; pulls a reason only on request; retries only on request.
 */
export function FeedbackChannel({
  uploadId,
  pages,
  checked = false,
  onSummary,
  noSheetSlot = null,
  flagsOnPage = false,
  anyDecided = false,
}: {
  readonly uploadId: string;
  readonly pages: readonly ChannelPage[];
  readonly checked?: boolean;
  /** Told which lines are flagged (only ever the ones the open call showed) and whether the question sheet is missing. */
  readonly onSummary?: (summary: ChannelSummary) => void;
  /** Where to draw the "question sheet unavailable" card instead of in this section (the top of the notes column). */
  readonly noSheetSlot?: HTMLElement | null;
  /** The flags that have a page are drawn on it by the caller (#661), so this section keeps its states only. */
  readonly flagsOnPage?: boolean;
  /** Some note of the sheet was decided (✓ or ✗): the card then says the second reader could not run, not that nothing was checked. */
  readonly anyDecided?: boolean;
}): React.JSX.Element | null {
  const api = useApi();
  const queryClient = useQueryClient();
  const [reasons, setReasons] = useState<Record<string, ReasonState>>({});
  const channel = useQuery({
    queryKey: queryKeys.homeworkFeedbackChannel(uploadId),
    queryFn: () => api.getHomeworkFeedbackChannel(uploadId),
    refetchInterval: (query) => (query.state.data?.feedbackState === 'pending' ? POLL_INTERVAL_MS : false),
  });
  const retry = useMutation({
    mutationFn: () => api.retryHomeworkFeedback(uploadId, crypto.randomUUID()),
    onSuccess: (response) => queryClient.setQueryData(queryKeys.homeworkFeedbackChannel(uploadId), response),
  });
  // The showing of the flags (recorded by the server). `shown` is what the open call returned, and is what is drawn.
  const [shown, setShown] = useState<HomeworkFeedbackChannelResponse | undefined>(undefined);
  const [openFailed, setOpenFailed] = useState(false);
  const requestedRun = useRef<string | null>(null);
  const open = useMutation({
    mutationFn: () => api.openHomeworkFeedbackChannel(uploadId, crypto.randomUUID()),
    onSuccess: (response) => {
      setOpenFailed(false);
      setShown(response);
    },
    onError: () => setOpenFailed(true),
  });
  const producedRun = channel.data?.feedbackState === 'produced' ? channel.data.feedbackRunId : null;
  // Once per produced review: the section is displayed, so the lines are shown, so the showing is recorded.
  useEffect(() => {
    if (producedRun === null || requestedRun.current === producedRun) return;
    requestedRun.current = producedRun;
    open.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `open` is a new object each render; the run id is the trigger.
  }, [producedRun]);
  const why = async (regionId: string): Promise<void> => {
    setReasons((current) => ({ ...current, [regionId]: { status: 'loading' } }));
    try {
      const response = await api.showHomeworkFeedbackReason(uploadId, regionId, crypto.randomUUID());
      setReasons((current) => ({ ...current, [regionId]: { status: 'shown', reason: response.reason } }));
    } catch {
      setReasons((current) => ({ ...current, [regionId]: { status: 'failed' } }));
    }
  };
  // What the page may read: only lines that are drawn, which a produced review's open response alone supplies (see the module comment).
  const drawn =
    channel.data?.feedbackState !== 'produced'
      ? []
      : shown !== undefined && shown.feedbackRunId === channel.data.feedbackRunId && !shown.flagsHidden
        ? shown.flags.filter((flag) => flag.flag === 'check_this').map((flag) => flag.regionId)
        : [];
  const flaggedKey = drawn.join('\u0000');
  const drawnFlags: Flag[] =
    channel.data?.feedbackState !== 'produced' || shown === undefined || shown.feedbackRunId !== channel.data.feedbackRunId || shown.flagsHidden
      ? []
      : shown.flags.filter((flag) => flag.flag === 'check_this');
  const latest = useRef({ flags: drawnFlags, why });
  latest.current = { flags: drawnFlags, why };
  const whyStable = useRef((regionId: string): void => void latest.current.why(regionId));
  const noSheetNow = channel.data?.feedbackState === 'failed' && channel.data.failureKind === NO_SHEET;
  const withoutSheetNow = channel.data?.feedbackState === 'produced' && channel.data.withoutQuestionSheet;
  useEffect(() => {
    onSummary?.({ flaggedRegionIds: flaggedKey === '' ? [] : flaggedKey.split('\u0000'), noSheet: noSheetNow, withoutSheet: withoutSheetNow, flags: latest.current.flags, reasons, why: whyStable.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the two values below are the whole content of the summary
  }, [flaggedKey, noSheetNow, withoutSheetNow, reasons]);
  if (channel.data === undefined) return null;
  // A produced review is drawn from the open response only; a read's flags are never drawn (see the module comment).
  const view: HomeworkFeedbackChannelResponse =
    channel.data.feedbackState !== 'produced'
      ? channel.data
      : shown !== undefined && shown.feedbackRunId === channel.data.feedbackRunId && !shown.flagsHidden
        ? shown
        : { ...channel.data, flags: [] };
  const hoist = noSheetNow && noSheetSlot !== null;
  return (
    <>
      {hoist ? createPortal(<NoSheetCard onRetry={() => retry.mutate()} retrying={retry.isPending} anyDecided={anyDecided} />, noSheetSlot) : null}
      <FeedbackChannelView
        hoistNoSheet={hoist}
        flagsOnPage={flagsOnPage}
        channel={view}
        pages={pages}
        reasons={reasons}
        retrying={retry.isPending}
        checked={checked}
        onWhy={(id) => void why(id)}
        onRetry={() => retry.mutate()}
        openFailed={openFailed}
        onOpen={() => {
          setOpenFailed(false);
          open.mutate();
        }}
      />
    </>
  );
}
